import { makeAutoObservable, runInAction, onBecomeObserved, toJS } from 'mobx'
import { SegmentService } from '../services/segmentService'
import { SegmentDto, SegmentReach, SegmentStatus, Device } from '../types'
import { AdStatus } from '../types/AdPlatforms'
import { ToastType } from '../types/ToastData'
import { RootStore } from './rootStore'
import { adminService } from '../services/adminService'
import {
    BasePubmaticSegment,
    CategoryItem,
    FrequencyCap,
    ImpressionTracker,
    Retargeting,
    RetargetingDefinition,
    Segment,
    SegmentType,
    TargetingDto
} from '../types/Segment'
import { Adtech } from '../types/Pricing'
import { HttpStatusCode } from '../services/types'
import { downloadFile } from '../utils/download'
import { InterestScore } from '../utils/scoreHelpers'
import { isProduction } from '../config'
import { InsightsService } from '../services/insightsService'
import { checkQueryParam } from '../utils/queryParam'

export type CurrentSegment = {
    id?: number
    companyId?: number
    status: SegmentStatus
    size: number
    targeting?: TargetingDto
    retargeting?: Retargeting | null
    events?: { id: number; name: string }[]
    durationDays: number
    reportUrl?: string
    segmentData?: Record<string, any>
    type: SegmentType
    config?: {
        gam?: {
            ppsEnabled?: boolean // deprecated
        }
        public?: boolean
        aiId?: string
        audienceLift?: {
            fb?: boolean
            gtm?: boolean
        }
    }
}

export class SegmentStore {
    private segmentService: SegmentService
    private insightsService: InsightsService
    private rootStore: RootStore

    languages: string[] = []
    categories: CategoryItem[] = []
    osList: Device[] | null = null
    segments: Segment[] = []
    segmentsReach: SegmentReach[] = []
    locations: any = null
    error: string | null = null
    equativSegment: boolean = false
    xandrCurateSegment: boolean = false
    xandrMonetizeSegment: boolean = false
    criteoSegment: boolean = false
    gamSegment: boolean = false
    indexExchangeSegment: boolean = false
    oneTagSegment: boolean = true
    pubmaticSegment: BasePubmaticSegment | null = null
    isBeingPublished: boolean = false
    maxUsersNumber = 0
    allSegments: any[] = []
    currentSegment?: CurrentSegment | null = null
    adminSegment: any = null
    lookalikeCategories: Record<string, number> | null = null
    impressionTracker: ImpressionTracker | null = null

    constructor(rootStore: RootStore, segmentService: SegmentService, insightsService: InsightsService) {
        this.segmentService = segmentService
        this.insightsService = insightsService
        this.rootStore = rootStore

        makeAutoObservable(this)
        this.resetCurrentSegment()

        onBecomeObserved(this, 'languages', async () => {
            if (this.languages.length > 0) return
            const res = await this.segmentService.getLanguages()
            runInAction(() => {
                const brokenLanguages = [
                    'ace',
                    'ach',
                    'ckb',
                    'enus',
                    'hr',
                    'kpe',
                    'la',
                    'lij',
                    'mo',
                    'other',
                    'sat',
                    'sc',
                    'scn',
                    'sm',
                    'tl',
                    'und',
                    'us',
                    'yue',
                    'zz',
                    'an',
                    'apw',
                    'eng',
                    'gb',
                    'sh'
                ]
                this.languages = res ? res.filter((x) => !brokenLanguages.includes(x)) : []
            })
        })
        onBecomeObserved(this, 'categories', async () => {
            if (this.categories.length > 0) return
            const res = await this.segmentService.getCategories()
            runInAction(() => {
                this.categories = res || []
            })
        })
        onBecomeObserved(this, 'osList', async () => {
            if (this.osList) return
            const res = await this.segmentService.getOsList()
            runInAction(() => {
                this.osList = res
            })
        })
        onBecomeObserved(this, 'segmentsReach', async () => {
            if (this.segmentsReach.length > 0) return
            const res = this.rootStore.companyStore.isDemo ? await this.segmentService.getDemoReach() : await this.segmentService.getReach()

            runInAction(() => {
                this.rootStore.appStore.incrementRequests()
                this.segmentsReach = res
                this.rootStore.appStore.decrementRequests()
            })
        })
        onBecomeObserved(this, 'locations', async () => {
            if (this.locations) return
            const res = await this.segmentService.getLocations()
            runInAction(() => {
                this.locations = res
            })
        })
        onBecomeObserved(this, 'maxUsersNumber', async () => {
            if (this.maxUsersNumber) return

            const dto = this.mapCurrentToSegmentDto()
            dto.name = 'test'
            dto.companyId = this.currentSegment?.companyId || this.rootStore.companyStore.myCompany.company.id
            dto.targeting = {
                language: null,
                categoryIds: null,
                deviceOs: null,
                deviceBrowser: null,
                deviceType: null,
                country: null,
                city: null,
                keyword: null,
                topicIds: null,
                gender: null,
                networth: null,
                lookalikeCats: null,
                adjacentTopicIds: null,
                sizeRecency: ['180'],
                engagement: null
            }

            runInAction(() => {
                this.maxUsersNumber = dto.size
            })
        })
    }

    private getAllCompanySegments = async () => {
        const res = await this.segmentService.getAll()

        runInAction(() => {
            this.rootStore.appStore.incrementRequests()
            this.segments = res ? res : []
            this.rootStore.appStore.decrementRequests()
        })
    }

    loadSegments = async (forceRefresh = false) => {
        if (this.segments.length > 0 && !forceRefresh) return
        return this.getAllCompanySegments()
    }

    convertLocalDateToUTC(localDate: Date, endDate: boolean) {
        const utcDate = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000)

        if (endDate) {
            utcDate.setUTCHours(23)
            utcDate.setUTCMinutes(59)
            utcDate.setUTCSeconds(59)
            utcDate.setUTCMilliseconds(999)
        }

        return utcDate.toISOString()
    }

    convertUTCDateToLocal(utcDate: string) {
        const localDate = new Date(utcDate)
        if (localDate.getUTCHours() > 0) {
            localDate.setUTCHours(0)
            localDate.setUTCMinutes(0)
            localDate.setUTCSeconds(0)
            localDate.setUTCMilliseconds(0)

            const offset = new Date().getTimezoneOffset() * 60 * 1000
            const localDate1 = new Date(localDate.getTime() - offset)
            localDate1.setHours(0, 0, 0, 0)

            return localDate1
        }
        if (localDate.getUTCHours() === 0) {
            const offset = new Date().getTimezoneOffset() * 60 * 1000
            const localDate2 = new Date(localDate.getTime() - offset)

            localDate2.setHours(0, 0, 0, 0)

            return localDate2
        }
    }

    resetCurrentSegment() {
        runInAction(() => {
            let retargeting: Retargeting | null = null
            let events: { id: number; name: string }[] | undefined

            if (checkQueryParam('type', SegmentType.Retargeting)) {
                retargeting = {
                    definition: {
                        properties: []
                    },
                    domains: []
                }
                events = []
            }

            this.currentSegment = {
                status: SegmentStatus.Draft,
                type: SegmentType.Targeting,
                size: 0,
                durationDays: 120,
                retargeting,
                events,
                companyId: this.rootStore.companyStore?.myCompany?.company?.id,
                targeting: {
                    categoryIds: null,
                    city: null,
                    country: null,
                    deviceBrowser: null,
                    deviceOs: null,
                    deviceType: null,
                    keyword: null,
                    language: null,
                    sizeRecency: null,
                    topicIds: null,
                    gender: null,
                    networth: null,
                    lookalikeCats: null,
                    adjacentTopicIds: null,
                    engagement: null
                },
                segmentData: {
                    name: '',
                    domains: []
                }
            }

            this.setImpressionTracker(null)
        })
    }

    get segment() {
        return this.mapCurrentToSegmentDto()
    }

    private mapDtoToCurrent(dto: SegmentDto): CurrentSegment {
        const { id, companyId, status, size, durationDays, targeting, retargeting, events, type, config } = dto

        const res: CurrentSegment = {
            id: id,
            companyId,
            status,
            size,
            type,
            durationDays,
            targeting,
            retargeting,
            events,
            config: config ?? undefined,
            segmentData: {
                name: dto.name
            }
        }

        return res
    }

    private mapCurrentToSegmentDto() {
        const { segmentData, id, companyId, status, size, durationDays, targeting, retargeting, events, type, config } = this.currentSegment!

        const dto: SegmentDto = {
            id,
            companyId: (companyId ?? -1) >= 0 ? companyId! : this.rootStore.companyStore?.myCompany?.company?.id,
            size,
            type,
            durationDays,
            name: segmentData?.name ?? '',
            status: status,
            targeting,
            retargeting,
            events,
            config: config ?? undefined
        }
        if (retargeting) {
            delete dto.definition
        } else {
            // set default engagement if categories selected
            if (dto?.targeting?.categoryIds?.length && !dto?.targeting?.engagement) {
                dto.targeting.engagement = InterestScore.Moderate
            }

            // clear engagement if there are no selected categories
            if (!dto?.targeting?.categoryIds?.length && dto?.targeting?.engagement) {
                dto.targeting.engagement = null
            }

            delete dto.retargeting
        }
        return dto
    }
    setSegmentName = (name: string) => {
        this.currentSegment!.segmentData!.name = name
    }
    setSegmentFilters(data: TargetingDto) {
        this.currentSegment!.targeting = data
    }
    setSegmentData(data: Record<string, string | number[]>) {
        this.currentSegment!.segmentData = data
    }
    setDurationData(data: number) {
        this.currentSegment!.durationDays = data
    }
    setRetargeting(data: Retargeting) {
        this.currentSegment!.retargeting = data
    }
    setLookalikeCats(data: string[] | null) {
        this.currentSegment!.targeting!.lookalikeCats = data
    }
    setInterestScore(data: InterestScore) {
        this.currentSegment!.targeting!.engagement = data
    }
    setSegmentSize(data: number) {
        this.currentSegment!.size = data
    }
    setRetargetingDifinition(data: RetargetingDefinition) {
        this.currentSegment!.retargeting!.definition = data
    }
    setType(data: SegmentType) {
        this.currentSegment!.type = data
    }
    // deprecated
    setConfig(data: { gam?: { ppsEnabled?: boolean }; public?: boolean }) {
        this.currentSegment!.config = data
    }
    setDomains(data: string[]) {
        this.currentSegment!.retargeting!.domains = data
    }
    setEvents(data: { id: number; name: string }[]) {
        this.currentSegment!.events = data
    }
    setCompanyId(data: number) {
        this.currentSegment!.companyId = data
    }

    async getSegments() {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const segments = await this.segmentService.getAll()
            runInAction(() => {
                this.segments = segments
            })
            return segments
        } catch (error) {
            runInAction(() => {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Failed to get segments'
                })
                runInAction(() => {
                    this.error = error instanceof Error ? error.message : String(error)
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async getSegment(id: number): Promise<Segment | undefined> {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            return this.rootStore.authStore.isAdmin ? await adminService.getSegmentAdmin(id) : await this.segmentService.getSegmentById(id)
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async setCurrentSegment(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const isAdmin = this.rootStore.authStore.isAdmin
            const resp = isAdmin ? await adminService.getSegmentAdmin(id) : await this.segmentService.getSegmentById(id)
            const current: SegmentDto | undefined = resp?.segment
            if (isAdmin) {
                runInAction(() => {
                    this.adminSegment = resp
                })
            }
            if (current) {
                const validCurrent = current
                runInAction(() => {
                    if (validCurrent.type !== SegmentType.Retargeting && validCurrent.id) {
                        this.getImpressionTracker(validCurrent.id)
                    }
                    this.currentSegment = this.mapDtoToCurrent(validCurrent)
                })
            } //TODO: else - throw 404!
        } catch (error) {
            // eslint-disable-next-line
            console.error(error)
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async save() {
        if (this.currentSegment?.id) {
            await this.update(false)
        }
    }

    async createEmpty(name: string, clonedSegment?: SegmentDto) {
        runInAction(() => {
            this.error = null
        })
        const dto = this.mapCurrentToSegmentDto()
        dto.name = name
        dto.segmentData = {
            name: dto.name
        }
        if (this.rootStore.appStore.isCreateRetargetingSegment) {
            dto.retargeting = {
                definition: {
                    properties: []
                }
            }
            dto.events = []
            dto.type = SegmentType.Retargeting
            delete dto.targeting
        } else {
            dto.targeting = {
                language: null,
                categoryIds: null,
                deviceOs: null,
                deviceBrowser: null,
                deviceType: null,
                country: null,
                city: null,
                keyword: null,
                topicIds: null,
                gender: null,
                networth: null,
                lookalikeCats: null,
                adjacentTopicIds: null,
                engagement: null,
                sizeRecency: ['180']
            }
        }

        if (clonedSegment) {
            const _c = toJS(clonedSegment)
            dto.targeting = _c.targeting
            dto.retargeting = _c.retargeting
            dto.events = _c.events
            dto.type = _c.type
            dto.lookalikeCats = _c.lookalikeCats
            dto.durationDays = _c.durationDays
            dto.config = { ..._c?.config }
        }

        this.currentSegment = dto
    }

    async create() {
        runInAction(() => {
            this.error = null
        })
        const dto = this.mapCurrentToSegmentDto()

        try {
            this.rootStore.appStore.incrementRequests()

            const resp = await this.segmentService.create(dto)

            runInAction(() => {
                this.currentSegment!.id = resp.id
                this.currentSegment!.size = resp.size
                this.currentSegment!.type = resp.type
                this.currentSegment!.durationDays = resp.durationDays
                this.segments.push({ prices: [], segment: resp })
                this.rootStore.appStore.setCreateRetargetingSegment(false)
            })
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to create the audience'
            })
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()

            this.rootStore.appStore.setToastNotification({
                type: ToastType.Success,
                title: 'Saved',
                body: 'The audience was successfully saved',
                isHidden: true
            })
        }
    }

    async updateSegmentConfig(updates: { id: number; config: SegmentDto['config'] }[]) {
        await Promise.all(
            updates.map(({ id, config }) => {
                return this.rootStore.authStore.isAdmin
                    ? adminService.updateSegmentConfigAdmin(id, config)
                    : this.segmentService.updateSegmentConfig(id, config)
            })
        )

        if (this.rootStore.authStore.isAdmin) {
            return
        }

        return this.getAllCompanySegments()
    }

    private async update(hideToast: boolean, isSilent = false) {
        runInAction(() => {
            this.error = null
        })

        const dto = this.mapCurrentToSegmentDto()

        try {
            this.rootStore.appStore.incrementRequests()

            // replace empty arrays with null
            if (dto.targeting) {
                const targeting = dto.targeting as Record<keyof TargetingDto, unknown>
                ;(Object.keys(targeting) as Array<keyof TargetingDto>).forEach((k) => {
                    if (Array.isArray(targeting[k]) && !(targeting[k] as unknown[]).length) {
                        targeting[k] = null
                    }
                })
            }

            const resp = await (this.rootStore.authStore.isAdmin ? adminService.updateSegmentAdmin(dto) : this.segmentService.update(dto))
            const index = this.segments.findIndex((x) => x.segment?.id === dto.id)
            runInAction(() => {
                this.currentSegment!.size = resp.size
                this.currentSegment!.durationDays = resp.durationDays
                if (index >= 0) {
                    this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                }
            })
        } catch (error) {
            const errMsg = error instanceof Error ? error.message : undefined
            runInAction(() => {
                this.error = errMsg ?? String(error)
            })

            // TODO: refactor the BE to have more control over errors formatting
            if (errMsg?.includes('failed: segment threshold is too low')) {
                this.showLowMauError()
            } else {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Saved',
                    body: errMsg ?? 'The audience was not saved',
                    isHidden: hideToast
                })
            }

            this.rootStore.appStore.decrementRequests()
        } finally {
            if (!this.error) {
                !isSilent &&
                    this.rootStore.appStore.setToastNotification({
                        type: ToastType.Success,
                        title: 'Saved',
                        body: 'The audience was successfully saved',
                        isHidden: hideToast
                    })
            }

            this.rootStore.appStore.decrementRequests()
        }
    }

    showLowMauError() {
        this.rootStore.appStore.setToastNotification({
            type: ToastType.Error,
            title: 'Not Activated',
            body: `You can not create segments with estimated size less than ${isProduction && !this.rootStore.authStore.isAdmin ? 1000 : 10}`,
            isHidden: false
        })
    }

    async estimate() {
        runInAction(() => {
            this.error = null
        })

        let resp: { size: number; singleSite: number }

        try {
            this.rootStore.appStore.incrementRequests()
            this.rootStore.appStore.setIsCurrentlyEstimated(true)
            const dto = this.mapCurrentToSegmentDto()

            dto.isAdv = this.rootStore.companyStore.isAdv

            resp = await this.insightsService.estimateClickHouse(dto)

            runInAction(() => {
                this.currentSegment!.size = resp.size
            })
        } catch (error) {
            const errMsg = error instanceof Error ? error.message : undefined
            runInAction(() => {
                this.error = errMsg ?? String(error)
            })
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Not Estimated',
                body: errMsg ?? 'The audience was not estimated'
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            this.rootStore.appStore.setIsCurrentlyEstimated(false)
        }
    }

    async delete(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()

            const index = this.segments.findIndex((x) => x?.segment.id === id)

            const companyId = index >= 0 ? this.rootStore.companyStore.myCompany.company.id : this.adminSegment.companyId

            this.rootStore.authStore.isAdmin ? await adminService.deleteSegmentAdmin(id, companyId) : await this.segmentService.delete(id)

            if (index >= 0) {
                runInAction(() => {
                    this.segments.splice(index, 1)
                })
            }
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to delete the audience'
            })
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async archive(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.archive(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)

            if (index >= 0) {
                runInAction(() => {
                    this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                    this.currentSegment!.status = resp.status
                })
            }
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async getSegmentExportStatus(companyId: number, segment: SegmentDto, adtech: Adtech) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = this.rootStore.authStore.isAdmin
                ? await adminService.getAdminSegmentExportStatus(companyId, segment.id!, adtech)
                : await this.segmentService.getSegmentExportStatus(segment.id!, adtech)
            runInAction(() => {
                switch (adtech) {
                    case Adtech.XandrCurate:
                        this.xandrCurateSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.XandrMonetize:
                        this.xandrMonetizeSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Equativ:
                        this.equativSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Criteo:
                        this.criteoSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.GAM:
                        this.gamSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Pubmatic:
                        this.pubmaticSegment = resp as any
                        break
                    case Adtech.Onetag:
                        this.oneTagSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.IndexExchange:
                        this.indexExchangeSegment = resp.status === AdStatus.Active
                        break
                }
            })
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async exportSegment(segmentId: number, adtech: Adtech) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            await this.segmentService.exportSegment(segmentId, adtech)
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not sent!',
                    body: 'Audience was not sent',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Success,
                    title: 'Sent!',
                    body: 'Audience was sent successfully',
                    isHidden: false
                })
            }
        }
    }

    async publish(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            this.isBeingPublished = true
            const resp = await this.segmentService.publish(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)
            runInAction(() => {
                if (index >= 0) {
                    this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                }
                this.currentSegment!.status = resp.status
            })
            return resp
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
                this.showLowMauError()
                this.isBeingPublished = false
                this.rootStore.appStore.decrementRequests()
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Activated!',
                    body: 'Audience was activated successfully',
                    isHidden: false
                })
                this.isBeingPublished = false
            }
        }
    }

    async schedule(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            this.isBeingPublished = true
            await this.update(true)
            if (this.error) {
                throw this.error
            }
            const resp = await this.segmentService.schedule(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)
            runInAction(() => {
                if (index >= 0) {
                    this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                }
                this.currentSegment!.status = resp.status
            })
            return resp
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Scheduled!',
                    body: 'Audience was not scheduled',
                    isHidden: false
                })
                this.isBeingPublished = false
                this.rootStore.appStore.decrementRequests()
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Scheduled!',
                    body: 'Audience was scheduled successfully',
                    isHidden: false
                })
                this.isBeingPublished = false
            }
        }
    }

    async downloadPPS(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const res = await this.segmentService.getPPS(id)

            downloadFile(res.message, `gampps_export_${new Date().toLocaleDateString()}.csv`)
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Donwloaded!',
                    body: 'PPS file was not downloaded',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async downloadPPSAdmin() {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const res = await this.segmentService.getPPSAdmin()

            downloadFile(res.message, `all_gampps_export_${new Date().toLocaleDateString()}.csv`)
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : String(error)
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Donwloaded!',
                    body: 'PPS file was not downloaded',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async createImpressionTracker(id: number, freqCap: FrequencyCap[]) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.createImpressionTracker(id, freqCap)

            runInAction(() => {
                this.setImpressionTracker(resp)
            })
            return resp
        } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error)
            runInAction(() => {
                this.error = errMsg
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Impression tracker was not created',
                    body: errMsg,
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Created!',
                    body: 'Impression tracker was created successfully',
                    isHidden: false
                })
            }
        }
    }

    async getImpressionTracker(id: number) {
        runInAction(() => {
            this.error = null

            // clear prev value
            this.setImpressionTracker(null)
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.getImpressionTracker(id)

            runInAction(() => {
                this.setImpressionTracker(resp)
            })
            return resp
        } catch (error) {
            //TODO: add error types
            //'Not found' impression tracker is valid  case, do not show the error
            if ((error as { status?: number }).status !== HttpStatusCode.NotFound) {
                runInAction(() => {
                    this.error = error instanceof Error ? error.message : String(error)
                })
            }
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    setImpressionTracker(value: ImpressionTracker | null) {
        this.impressionTracker = value
    }

    isCountriesSelectedWithoutCategories = () =>
        !!(
            this.currentSegment?.targeting?.country?.length &&
            !this.currentSegment?.targeting?.categoryIds?.length &&
            !this.currentSegment?.targeting?.deviceBrowser?.length &&
            !this.currentSegment?.targeting?.topicIds?.length &&
            !this.currentSegment?.targeting?.keyword?.length &&
            this.rootStore.companyStore?.myCompany?.company?.isAdvertiser
        )
}
